import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { useConfirm } from "../../components/ConfirmDialog";
import { PanelLoading } from "../../components/Loading";
import { LotExpiry } from "../../components/LotExpiry";
import { LotQualityBadge, type LotQualityStatus } from "../../components/LotQualityBadge";
import { Select } from "../../components/Select";
import { useToast } from "../../components/Toast";
import { formatDate, formatDateTime, formatNumber } from "../../lib/format";
import type { ApiError } from "../../api/client";
import { warehouseApi, type StatusFilter, type StockPage, type StockRow } from "../../api/warehouse";
import { LotHolds, ReserveForm } from "./LotReservations";
import { useOsveziStanje } from "./OsveziStanje";

const PAGE = 200;
const STATUSI: StatusFilter[] = ["OPEN", "CONFORMING", "DOMESTIC_ONLY", "NONCONFORMING"];
/** Količina sa jedinicom i datum ostaju u jednom redu: prelomljen „29. 8. / 2028." se čita kao dva podatka. */
const uRedu = { whiteSpace: "nowrap" } as const;

/**
 * Magacin: roba na stanju po skladištu, polju i LOT-u, onako kako je broji ERP (isti zbir kao Bankomova aplikacija
 * magacina), ali na jednom mestu sa artiklom, rokom, sledljivošću i statusom kvaliteta LOT-a.
 *
 * Redosled je redosled kojim roba treba da izlazi: po artiklu, a u okviru artikla prvo LOT koji pre ističe. Zbir
 * artikla je ceo zbir pod izabranim filterom, ne samo deo na ovoj strani. Filteri stoje u adresi, pa se „proizvodni
 * magacin, neobrađeni" šalje kao veza: to je spisak LOT-ova koje kvalitet tek treba da oceni.
 *
 * Status (usaglašen, SS, neusaglašen) daje šef kvaliteta, za LOT gde god mu palete stoje; SS i neusaglašen traže
 * razlog, jer ga prodaja čita kad LOT ne prođe.
 */
export default function WarehousePage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const toast = useToast();
  const { hasPermission } = useAuth();
  // Sledljivost drže kvalitet, direktor prodaje i otkup; ostalima LOT ostaje tekst, ne veza koja završava na zabrani.
  const mozeSledljivost = hasPermission("section:traceability:read");
  const mozeOdluku = hasPermission("lot_quality:decide");
  // Rezerviše prodaja (svojim kupcima), kvalitet i administratori; porudžbinu otvara samo onaj ko sme da je čita.
  const mozeRezervaciju = hasPermission("stock_reservations:manage");
  const mozePorudzbine = hasPermission("section:orders:read");
  const akcije = mozeOdluku || mozeRezervaciju;
  const [param, setParam] = useSearchParams();
  const production = param.get("production") === "1";
  const warehouse = broj(param.get("warehouse"));
  const field = broj(param.get("field"));
  const status = statusIz(param.get("status"));
  const strana = broj(param.get("page")) ?? 0;
  const q = param.get("q") ?? "";
  const [upit, setUpit] = useState(q);
  const [data, setData] = useState<StockPage | null>(null);
  const [greska, setGreska] = useState(false);
  const [osvezi, setOsvezi] = useState(0);
  // „Osveži stanje“: čitanje iz ERP-a odmah, praćeno korak po korak; uspeh ponovo čita spisak.
  const osvezavanje = useOsveziStanje(() => setOsvezi((n) => n + 1));
  // Odluka koja čeka razlog: SS i neusaglašen se ne daju bez njega.
  const [razlogZa, setRazlogZa] = useState<{ kljuc: string; lotId: string; status: LotQualityStatus } | null>(null);
  const [razlog, setRazlog] = useState("");
  const [radi, setRadi] = useState<string | null>(null);
  // Red ispod kog je otvorena rezervacija.
  const [rezervisi, setRezervisi] = useState<string | null>(null);
  // LOT-ovi izabrani za grupnu ocenu: po LOT-u, ne po poziciji, pa se LOT u dva polja bira jednom.
  const [izabrani, setIzabrani] = useState<Set<string>>(new Set());
  const confirm = useConfirm();

  const postavi = (izmene: Record<string, string | null>) => {
    const sledeci = new URLSearchParams(param);
    for (const [k, v] of Object.entries(izmene)) {
      if (v == null || v === "") sledeci.delete(k);
      else sledeci.set(k, v);
    }
    // Novi filter počinje od prve strane.
    if (!("page" in izmene)) sledeci.delete("page");
    setParam(sledeci, { replace: true });
  };

  // Pretraga ide posle kratke pauze, da svaki pritisak tastera ne bude upit.
  const pauza = useRef<number | undefined>(undefined);
  useEffect(() => {
    window.clearTimeout(pauza.current);
    if (upit.trim() === q) return;
    pauza.current = window.setTimeout(() => postavi({ q: upit.trim() || null }), 300);
    return () => window.clearTimeout(pauza.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upit]);

  useEffect(() => {
    let vazi = true;
    setGreska(false);
    // Izbor važi za ono što je na ekranu; nova strana ili filter ga počinje iznova.
    setIzabrani(new Set());
    warehouseApi
      .stock({ warehouse, field, production, status, q, page: strana, size: PAGE })
      .then((d) => {
        if (vazi) setData(d);
      })
      .catch(() => {
        if (vazi) {
          setData(null);
          setGreska(true);
        }
      });
    return () => {
      vazi = false;
    };
  }, [warehouse, field, production, status, q, strana, osvezi]);

  async function odluci(lotId: string, novi: LotQualityStatus, napomena: string | null) {
    setRadi(lotId);
    try {
      const v = await warehouseApi.decide(lotId, novi, napomena);
      toast.success(t("warehouse.decided", "LOT {{lot}}: {{status}}", { lot: v.lot ?? "", status: statusTekst(t, novi) }));
      setRazlogZa(null);
      setRazlog("");
      setOsvezi((n) => n + 1);
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setRadi(null);
    }
  }

  /**
   * Grupna ocena: izabrani LOT-ovi postaju usaglašeni odjednom, posle potvrde. Samo usaglašen, jer SS i neusaglašen
   * traže razlog, koji prodaja čita, za svaki LOT posebno.
   */
  async function odluciGrupno() {
    const lotovi = [...izabrani];
    if (lotovi.length === 0) return;
    const potvrda = await confirm({
      title: t("warehouse.bulk.confirmTitle", "Grupna ocena"),
      message: t(
        "warehouse.bulk.confirmBody",
        "Označiti {{n}} LOT-ova kao usaglašene? Svaka odluka ide u trag izmena, pod vašim imenom.",
        { n: lotovi.length },
      ),
      confirmLabel: t("warehouse.action.CONFORMING", "Usaglašen"),
    });
    if (!potvrda) return;
    setRadi("grupno");
    try {
      const odluke = await warehouseApi.decideMany(lotovi, "CONFORMING");
      toast.success(t("warehouse.bulk.done", "Usaglašeno LOT-ova: {{n}}.", { n: odluke.length }));
      setIzabrani(new Set());
      setOsvezi((n) => n + 1);
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setRadi(null);
    }
  }

  function preklopi(lotId: string) {
    setIzabrani((pre) => {
      const sledeci = new Set(pre);
      if (sledeci.has(lotId)) sledeci.delete(lotId);
      else sledeci.add(lotId);
      return sledeci;
    });
  }

  const izabrano = data?.warehouses.find((w) => w.id === warehouse) ?? null;
  const skladista = data?.warehouses ?? [];
  const polja = (izabrano ? [izabrano] : skladista).flatMap((w) =>
    w.fields.map((f) => ({
      value: String(f.id),
      label: `${f.name || "—"}${f.store ? ` · ${f.store}` : ""} (${f.positions})`,
      group: izabrano ? undefined : w.name ?? undefined,
    })),
  );
  const zbir = new Map((data?.productTotals ?? []).map((p) => [p.erpProductId, p]));
  const grupe = poArtiklu(data?.rows ?? []);
  const od = data && data.total > 0 ? data.page * data.size + 1 : 0;
  const do_ = data ? Math.min(data.total, (data.page + 1) * data.size) : 0;
  const kolona = (akcije ? 8 : 7) + (mozeOdluku ? 1 : 0);
  // Za grupnu ocenu se bira LOT koji još nije usaglašen.
  const zaIzbor = mozeOdluku
    ? [...new Set((data?.rows ?? []).filter((r) => r.goodsLotId && r.qualityStatus !== "CONFORMING").map((r) => r.goodsLotId!))]
    : [];
  const sviIzabrani = zaIzbor.length > 0 && zaIzbor.every((id) => izabrani.has(id));
  // Rezervacije su LOT-ove, ne pozicije: pišu se jednom, ispod prvog reda LOT-a na strani.
  const prviRedLota = new Map<string, string>();
  for (const r of data?.rows ?? []) {
    if (!prviRedLota.has(r.erpLotId)) prviRedLota.set(r.erpLotId, `${r.warehouseId}-${r.fieldId}-${r.erpLotId}`);
  }
  const broji = (s: StatusFilter) =>
    data?.statusCounts
      ? {
          OPEN: data.statusCounts.open,
          CONFORMING: data.statusCounts.conforming,
          DOMESTIC_ONLY: data.statusCounts.domesticOnly,
          NONCONFORMING: data.statusCounts.nonconforming,
        }[s]
      : null;

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("warehouse.title", "Magacin")}</h1>
          <div className="sub">
            {t("warehouse.lead", "Roba na stanju po skladištu, polju i LOT-u, kako je broji ERP.")}{" "}
            {data?.syncedAt
              ? t("warehouse.syncedAt", "Stanje pročitano iz ERP-a {{at}}.", { at: formatDateTime(data.syncedAt, lang) })
              : data
                ? t("warehouse.notSynced", "Stanje iz ERP-a još nije pročitano.")
                : null}
          </div>
          {data?.failedAt && (
            <div style={{ marginTop: 6 }}>
              <span className="tag warn">
                {t("warehouse.failed", "Poslednje čitanje ERP-a nije uspelo ({{at}}), pa je prikazano starije stanje.", {
                  at: formatDateTime(data.failedAt, lang),
                })}
              </span>
            </div>
          )}
        </div>
        <div className="page-actions">{osvezavanje.dugme}</div>
      </div>
      {osvezavanje.panel}

      <div className="card">
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div className="toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
            <div className="seg">
              <button type="button" aria-pressed={!production} onClick={() => postavi({ production: null })}>
                {t("warehouse.all", "Sva roba")}
              </button>
              <button
                type="button"
                aria-pressed={production}
                onClick={() => postavi({ production: "1" })}
                title={t(
                  "warehouse.productionHint",
                  "Polja „-0“ i polja u koja realizacija proizvodnje knjiži LOT pravo (premiksi u 9-1, rinfuz u ćelije), dok LOT tu stoji.",
                )}
              >
                {t("warehouse.production", "Proizvodni magacin")}
              </button>
            </div>
            <Select
              value={warehouse == null ? "" : String(warehouse)}
              onChange={(v) => postavi({ warehouse: v || null, field: null })}
              ariaLabel={t("warehouse.warehouse", "Skladište")}
              options={[
                { value: "", label: t("warehouse.allWarehouses", "sva skladišta") },
                ...skladista.map((w) => ({
                  value: String(w.id),
                  label: `${w.name || w.id} (${w.fields.reduce((n, f) => n + f.positions, 0)})`,
                })),
              ]}
            />
            <Select
              value={field == null ? "" : String(field)}
              onChange={(v) => postavi({ field: v || null })}
              ariaLabel={t("warehouse.field", "Polje")}
              options={[{ value: "", label: t("warehouse.allFields", "sva polja") }, ...polja]}
            />
            <input
              className="input"
              value={upit}
              onChange={(e) => setUpit(e.target.value)}
              placeholder={t("warehouse.search", "artikal, šifra ili LOT")}
              aria-label={t("warehouse.search", "artikal, šifra ili LOT")}
            />
          </div>
          {/* Statusi kvaliteta: broje se LOT-ovi, ne pozicije, pod ostalim izabranim filterima. */}
          <div className="seg" role="group" aria-label={t("warehouse.statusFilter", "Status kvaliteta")}>
            <button type="button" aria-pressed={status == null} onClick={() => postavi({ status: null })}>
              {t("warehouse.statusAll", "Svi statusi")}
            </button>
            {STATUSI.map((s) => (
              <button key={s} type="button" aria-pressed={status === s} onClick={() => postavi({ status: s })}>
                {filterTekst(t, s)}
                {broji(s) != null ? ` (${formatNumber(broji(s), lang)})` : ""}
              </button>
            ))}
          </div>
        </div>
      </div>

      {greska ? (
        <div className="card">
          <div className="empty">{t("warehouse.loadFailed", "Stanje nije moglo da se učita.")}</div>
        </div>
      ) : data === null ? (
        <PanelLoading text={t("warehouse.loading", "Čitam stanje…")} />
      ) : data.rows.length === 0 ? (
        <div className="card">
          <div className="empty">
            {data.syncedAt
              ? t("warehouse.none", "Nema robe za ove uslove.")
              : t("warehouse.notSynced", "Stanje iz ERP-a još nije pročitano.")}
          </div>
        </div>
      ) : (
        <>
          {mozeOdluku && izabrani.size > 0 && (
            <div className="card" style={grupnaTraka} role="region" aria-label={t("warehouse.bulk.label", "Grupna ocena")}>
              <b>{t("warehouse.bulk.selected", "Izabrano LOT-ova: {{n}}", { n: izabrani.size })}</b>
              <span className="muted">
                {t("warehouse.bulk.hint", "Usaglašen se daje svima odjednom; SS i neusaglašen traže razlog za svaki LOT.")}
              </span>
              <div className="toolbar" style={{ gap: 8, marginLeft: "auto" }}>
                <button type="button" className="btn sm" onClick={() => setIzabrani(new Set())} disabled={radi === "grupno"}>
                  {t("warehouse.bulk.clear", "Poništi izbor")}
                </button>
                <button type="button" className="btn sm primary" onClick={() => void odluciGrupno()} disabled={radi === "grupno"}>
                  {t("warehouse.action.CONFORMING", "Usaglašen")}
                </button>
              </div>
            </div>
          )}
          <div className="card" style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead>
                <tr>
                  {mozeOdluku && (
                    <th style={{ width: 32 }}>
                      <input
                        type="checkbox"
                        checked={sviIzabrani}
                        disabled={zaIzbor.length === 0}
                        onChange={() => setIzabrani(sviIzabrani ? new Set() : new Set(zaIzbor))}
                        aria-label={t("warehouse.bulk.selectAll", "Izaberi sve LOT-ove na strani koji još nisu usaglašeni")}
                      />
                    </th>
                  )}
                  <th>{t("warehouse.col.lot", "LOT")}</th>
                  <th>{t("warehouse.col.place", "Skladište · polje")}</th>
                  <th className="num">{t("warehouse.col.quantity", "Količina")}</th>
                  <th>{t("warehouse.col.expires", "Ističe")}</th>
                  <th>{t("warehouse.col.quality", "Kvalitet")}</th>
                  <th>{t("warehouse.col.firstIn", "Prvi ulaz")}</th>
                  <th>{t("warehouse.col.lastMove", "Poslednje kretanje")}</th>
                  {akcije && <th />}
                </tr>
              </thead>
              <tbody>
                {grupe.map((g) => {
                  const ukupno = zbir.get(g.erpProductId);
                  return (
                    <Fragment key={`${g.erpProductId}-${g.rows[0].erpLotId}`}>
                      <tr className="band">
                        <td colSpan={kolona}>
                          {g.productName || "—"}{" "}
                          <span className="muted" style={{ fontWeight: 400 }}>
                            · {t("warehouse.code", "šifra {{code}}", { code: g.erpProductId })}
                            {ukupno &&
                              ` · ${t("warehouse.total", "ukupno {{qty}}, pozicija: {{n}}", {
                                qty: `${formatNumber(ukupno.quantity, lang, { maximumFractionDigits: 3 })} ${g.unit ?? ""}`.trim(),
                                n: ukupno.positions,
                              })}`}
                          </span>
                        </td>
                      </tr>
                      {g.rows.map((r) => {
                        const kljuc = `${r.warehouseId}-${r.fieldId}-${r.erpLotId}`;
                        return (
                          <Fragment key={kljuc}>
                            <tr>
                              {mozeOdluku && (
                                <td>
                                  {r.goodsLotId && r.qualityStatus !== "CONFORMING" && (
                                    <input
                                      type="checkbox"
                                      checked={izabrani.has(r.goodsLotId)}
                                      onChange={() => preklopi(r.goodsLotId!)}
                                      aria-label={t("warehouse.bulk.selectLot", "Izaberi LOT {{lot}}", { lot: r.lot ?? "" })}
                                    />
                                  )}
                                </td>
                              )}
                              <td className="mono">
                                {mozeSledljivost && r.goodsLotId ? (
                                  <Link to={`/traceability?lot=${r.goodsLotId}`}>{r.lot}</Link>
                                ) : (
                                  r.lot
                                )}
                              </td>
                              <td>
                                {r.warehouseName} · <b>{r.fieldName || "—"}</b>{" "}
                                {r.production && (
                                  <span className="tag info">{t("warehouse.productionTag", "proizvodni")}</span>
                                )}
                                {r.storeName && <div className="muted">{r.storeName}</div>}
                              </td>
                              <td className="num" style={r.quantity < 0 ? { ...uRedu, color: "var(--err)" } : uRedu}>
                                {formatNumber(r.quantity, lang, { maximumFractionDigits: 3 })} {r.unit}
                                {(r.lotReserved ?? 0) > 0 && (
                                  <div className="muted" style={(r.lotFree ?? 0) < 0 ? { color: "var(--err)" } : undefined}>
                                    {t("warehouse.reserved", "rezervisano {{qty}} · slobodno {{free}}", {
                                      qty: formatNumber(r.lotReserved, lang, { maximumFractionDigits: 3 }),
                                      free: formatNumber(r.lotFree, lang, { maximumFractionDigits: 3 }),
                                    })}
                                  </div>
                                )}
                              </td>
                              <td style={uRedu}>
                                <LotExpiry iso={r.expiresOn} lang={lang} />
                              </td>
                              <td>
                                <LotQualityBadge status={r.qualityStatus} note={r.qualityNote} />
                                {r.qualityNote && <div className="muted">{r.qualityNote}</div>}
                                {r.qualityDecidedAt && (
                                  <div className="muted">
                                    {[r.qualityDecidedBy, formatDate(r.qualityDecidedAt, lang)].filter(Boolean).join(", ")}
                                  </div>
                                )}
                              </td>
                              <td style={uRedu}>{r.firstReceivedOn ? formatDate(r.firstReceivedOn, lang) : "—"}</td>
                              <td style={uRedu}>{r.lastMovedOn ? formatDate(r.lastMovedOn, lang) : "—"}</td>
                              {akcije && (
                                <td>
                                  {r.goodsLotId && (
                                    <div className="toolbar" style={{ gap: 6, justifyContent: "flex-end", flexWrap: "nowrap" }}>
                                      {mozeRezervaciju && (r.lotFree ?? 0) > 0 && r.qualityStatus !== "NONCONFORMING" && (
                                        <button
                                          type="button"
                                          className="btn sm"
                                          aria-expanded={rezervisi === kljuc}
                                          onClick={() => setRezervisi(rezervisi === kljuc ? null : kljuc)}
                                        >
                                          {t("warehouse.reserve", "Rezerviši")}
                                        </button>
                                      )}
                                      {(mozeOdluku ? (["CONFORMING", "DOMESTIC_ONLY", "NONCONFORMING"] as LotQualityStatus[]) : [])
                                        .filter((s) => s !== r.qualityStatus)
                                        .map((s) => (
                                          <button
                                            key={s}
                                            type="button"
                                            className="btn sm"
                                            disabled={radi === r.goodsLotId}
                                            onClick={() => {
                                              if (s === "CONFORMING") void odluci(r.goodsLotId!, s, null);
                                              else {
                                                setRazlogZa({ kljuc, lotId: r.goodsLotId!, status: s });
                                                setRazlog("");
                                              }
                                            }}
                                          >
                                            {akcijaTekst(t, s)}
                                          </button>
                                        ))}
                                    </div>
                                  )}
                                </td>
                              )}
                            </tr>
                            {prviRedLota.get(r.erpLotId) === kljuc && r.reservations.length > 0 && (
                              <tr>
                                <td colSpan={kolona} style={{ paddingTop: 0 }}>
                                  <LotHolds red={r} lang={lang} mozePorudzbine={mozePorudzbine} onChanged={() => setOsvezi((n) => n + 1)} />
                                </td>
                              </tr>
                            )}
                            {rezervisi === kljuc && (
                              <tr>
                                <td colSpan={kolona}>
                                  <ReserveForm
                                    red={r}
                                    lang={lang}
                                    onDone={() => {
                                      setRezervisi(null);
                                      setOsvezi((n) => n + 1);
                                    }}
                                    onCancel={() => setRezervisi(null)}
                                  />
                                </td>
                              </tr>
                            )}
                            {razlogZa?.kljuc === kljuc && (
                              <tr>
                                <td colSpan={kolona}>
                                  <div className="toolbar" style={{ gap: 8, flexWrap: "wrap" }}>
                                    <input
                                      className="input"
                                      style={{ minWidth: 280, flex: 1 }}
                                      value={razlog}
                                      onChange={(e) => setRazlog(e.target.value)}
                                      placeholder={t(
                                        "warehouse.reasonPlaceholder",
                                        "Razlog za {{status}}: prodaja ga čita",
                                        { status: statusTekst(t, razlogZa.status) },
                                      )}
                                      aria-label={t("warehouse.reason", "Razlog")}
                                    />
                                    <button type="button" className="btn sm" onClick={() => setRazlogZa(null)}>
                                      {t("common.cancel", "Odustani")}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn sm primary"
                                      disabled={!razlog.trim() || radi === razlogZa.lotId}
                                      onClick={() => void odluci(razlogZa.lotId, razlogZa.status, razlog.trim())}
                                    >
                                      {t("warehouse.saveDecision", "Sačuvaj")}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            <div className="card-b toolbar" style={{ justifyContent: "space-between", gap: 8 }}>
              <span className="muted">
                {t("warehouse.shown", "{{from}}–{{to}} od {{total}}", { from: od, to: do_, total: data.total })}
              </span>
              <div className="toolbar" style={{ gap: 6 }}>
                <button
                  type="button"
                  className="btn sm"
                  disabled={data.page === 0}
                  onClick={() => postavi({ page: data.page > 1 ? String(data.page - 1) : null })}
                >
                  {t("warehouse.prev", "Prethodna")}
                </button>
                <button
                  type="button"
                  className="btn sm"
                  disabled={do_ >= data.total}
                  onClick={() => postavi({ page: String(data.page + 1) })}
                >
                  {t("warehouse.next", "Sledeća")}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** Traka grupne ocene ostaje ispod gornje trake dok se spisak pomera, kao zaglavlje Registra. */
const grupnaTraka = {
  position: "sticky",
  top: "var(--topbar-h)",
  zIndex: 4,
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "6px 12px",
  padding: "10px 14px",
} as const;

type T = ReturnType<typeof useTranslation>["t"];

function statusTekst(t: T, s: LotQualityStatus): string {
  return s === "CONFORMING"
    ? t("lotQuality.status.CONFORMING", "usaglašen")
    : s === "DOMESTIC_ONLY"
      ? t("lotQuality.status.DOMESTIC_ONLY", "SS")
      : t("lotQuality.status.NONCONFORMING", "neusaglašen");
}

function filterTekst(t: T, s: StatusFilter): string {
  switch (s) {
    case "OPEN":
      return t("warehouse.filter.OPEN", "Neobrađeni");
    case "CONFORMING":
      return t("warehouse.filter.CONFORMING", "Usaglašeni");
    case "DOMESTIC_ONLY":
      return t("warehouse.filter.DOMESTIC_ONLY", "SS");
    case "NONCONFORMING":
      return t("warehouse.filter.NONCONFORMING", "Neusaglašeni");
  }
}

function akcijaTekst(t: T, s: LotQualityStatus): string {
  return s === "CONFORMING"
    ? t("warehouse.action.CONFORMING", "Usaglašen")
    : s === "DOMESTIC_ONLY"
      ? t("warehouse.action.DOMESTIC_ONLY", "SS")
      : t("warehouse.action.NONCONFORMING", "Neusaglašen");
}

/** Uzastopni redovi istog artikla, onako kako ih server šalje (po artiklu, pa po roku). */
function poArtiklu(rows: StockRow[]) {
  const grupe: { erpProductId: string; productName: string | null; unit: string | null; rows: StockRow[] }[] = [];
  for (const r of rows) {
    const poslednja = grupe[grupe.length - 1];
    if (poslednja && poslednja.erpProductId === r.erpProductId) poslednja.rows.push(r);
    else grupe.push({ erpProductId: r.erpProductId, productName: r.productName, unit: r.unit, rows: [r] });
  }
  return grupe;
}

function broj(v: string | null): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isInteger(n) ? n : null;
}

function statusIz(v: string | null): StatusFilter | null {
  return v && (STATUSI as string[]).includes(v) ? (v as StatusFilter) : null;
}
